import React, { useState } from 'react';
import { useTheme } from '../contexts/ThemeContext';
import {
  MailIcon,
  LockIcon,
  EyeIcon,
  EyeOffIcon,
  SunIcon,
  MoonIcon
} from '../components/common/Icons';

interface LoginPageProps {
  onLogin?: (email: string, password: string, rememberMe: boolean) => void;
  error?: string | null;
  onErrorClear?: () => void;
}

/**
 * Peakstead.ai logomark (WQ-334) — an original mark: a translucent back
 * ridge and a solid front peak with a doorway cut at its base ("peak" +
 * "stead"). Decorative; the adjacent wordmark carries the accessible name.
 */
const PeaksteadMark: React.FC = () => (
  <svg className="auth-brand-mark" viewBox="0 0 40 40" aria-hidden="true" focusable="false">
    <defs>
      <linearGradient id="peakstead-mark-gradient" gradientUnits="userSpaceOnUse" x1="0" y1="0" x2="40" y2="40">
        <stop offset="0" style={{ stopColor: 'var(--accent-primary)' }} />
        <stop offset="1" style={{ stopColor: 'var(--accent-secondary)' }} />
      </linearGradient>
    </defs>
    <rect width="40" height="40" rx="11" fill="url(#peakstead-mark-gradient)" />
    <path d="M5 30 L15.5 15 L26 30 Z" fill="#ffffff" fillOpacity="0.45" />
    <path d="M13 30 L24.5 10 L36 30 Z" fill="#ffffff" />
    <path d="M22 30 V25.2 a2.5 2.5 0 0 1 5 0 V30 Z" fill="url(#peakstead-mark-gradient)" />
  </svg>
);

interface SSODiscovery {
  provider: string;
  provider_name: string;
  tenant_name: string;
  tenant_id: string;
}

const LoginPage: React.FC<LoginPageProps> = ({ onLogin, error, onErrorClear }) => {
  const { theme, toggleTheme } = useTheme();
  // Initialize from localStorage synchronously for reliable behavior
  const [email, setEmail] = useState(() => {
    if (typeof window !== 'undefined') {
      return localStorage.getItem('remembered_email') || '';
    }
    return '';
  });
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(() => {
    if (typeof window !== 'undefined') {
      return !!localStorage.getItem('remembered_email');
    }
    return false;
  });
  const [isLoading, setIsLoading] = useState(false);

  // Email-first login flow state (WQ-071)
  const [loginStep, setLoginStep] = useState<'email' | 'sso' | 'password'>('email');
  const [ssoDiscovery, setSsoDiscovery] = useState<SSODiscovery | null>(null);
  const [isDiscovering, setIsDiscovering] = useState(false);
  const [ssoError, setSsoError] = useState<string | null>(null);

  const handleEmailNext = async () => {
    if (!email) return;

    setIsDiscovering(true);
    setSsoError(null);
    try {
      const response = await fetch(`/api/auth/sso/discover?email=${encodeURIComponent(email)}`);
      const data = await response.json();

      if (data.sso_available) {
        setSsoDiscovery(data);
        setLoginStep('sso');
      } else {
        setLoginStep('password');
      }
    } catch {
      // Discovery failed — fall through to password
      setLoginStep('password');
    } finally {
      setIsDiscovering(false);
    }
  };

  const handleSSOLogin = async () => {
    if (!ssoDiscovery) return;

    try {
      // WQ-077: Pass email for login_hint (forces account picker + pre-fills email)
      const response = await fetch(
        `/api/auth/sso/login?tenant=${ssoDiscovery.tenant_id}&email=${encodeURIComponent(email)}`
      );
      const data = await response.json();
      if (data.authorization_url) {
        window.location.href = data.authorization_url;
      } else {
        setSsoError('Failed to get SSO login URL');
      }
    } catch {
      setSsoError('Failed to initiate SSO login');
    }
  };

  const handleResetEmail = () => {
    setLoginStep('email');
    setEmail('');
    setPassword('');
    setSsoDiscovery(null);
    setSsoError(null);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // If on email step, do discovery first
    if (loginStep === 'email') {
      await handleEmailNext();
      return;
    }

    // Password step — normal login
    setIsLoading(true);
    if (onLogin) {
      onLogin(email, password, rememberMe);
    }
    setIsLoading(false);
  };

  return (
    <div className="auth-layout auth-layout-centered">
      {/* Background Effects — topographic contours + the existing orbs */}
      <div className="auth-bg-effects">
        <svg
          className="auth-contours"
          viewBox="0 0 1440 900"
          preserveAspectRatio="xMidYMid slice"
          aria-hidden="true"
          focusable="false"
        >
          <defs>
            <path
              id="auth-contour"
              d="M100 0 C100 45 70 95 20 100 C-35 106 -95 70 -100 15 C-105 -40 -60 -98 -5 -100 C55 -102 100 -50 100 0 Z"
              fill="none"
              stroke="currentColor"
              strokeWidth="1"
              vectorEffect="non-scaling-stroke"
            />
          </defs>
          <g transform="translate(1260 110)">
            {[0.5, 1, 1.5, 2, 2.5, 3, 3.5, 4].map((scale, i) => (
              <use key={scale} href="#auth-contour" transform={`translate(${i * -9} ${i * 7}) rotate(${i * 5}) scale(${scale})`} />
            ))}
          </g>
          <g transform="translate(170 820)">
            {[0.6, 1.15, 1.7, 2.25, 2.8, 3.35].map((scale, i) => (
              <use key={scale} href="#auth-contour" transform={`translate(${i * 11} ${i * -6}) rotate(${i * -6 + 40}) scale(${scale})`} />
            ))}
          </g>
        </svg>
        <div className="auth-orb auth-orb-1" />
        <div className="auth-orb auth-orb-2" />
        <div className="auth-orb auth-orb-3" />
      </div>

      {/* Theme Toggle */}
      <button
        className="theme-toggle auth-theme-toggle"
        onClick={toggleTheme}
        aria-label="Toggle theme"
      >
        {theme === 'dark' ? <SunIcon size={18} /> : <MoonIcon size={18} />}
      </button>

      {/* Centered column — Peakstead.ai brand over the single auth card (WQ-334) */}
      <main className="auth-center">
        <div className="auth-brand">
          <PeaksteadMark />
          <h1 className="auth-brand-wordmark" aria-label="Peakstead.ai">
            Peakstead<span className="auth-brand-tld">.ai</span>
          </h1>
        </div>

        <div className="auth-card">
          {/* Header */}
          <div className="auth-card-header">
            <h2 className="auth-card-title">Welcome back</h2>
            <p className="auth-card-subtitle">Sign in to continue to your dashboard</p>
          </div>

          {/* Auth Error Banner */}
          {error && (
            <div style={{
              padding: '12px 16px',
              background: 'rgba(239, 68, 68, 0.08)',
              border: '1px solid rgba(239, 68, 68, 0.2)',
              borderRadius: '8px',
              color: '#f87171',
              fontSize: '13px',
              marginBottom: '12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}>
              <span>{error}</span>
              <button onClick={onErrorClear} style={{
                background: 'none', border: 'none', color: '#f87171',
                cursor: 'pointer', fontSize: '16px', padding: '0 4px',
              }}>×</button>
            </div>
          )}

          {/* Login Form */}
          <form className="auth-form" onSubmit={handleSubmit}>

            {/* Step 1: Email */}
            {loginStep === 'email' && (
              <>
                <div className="input-group">
                  <label className="input-label" htmlFor="email">Email</label>
                  <div className="input-wrapper">
                    <MailIcon size={18} className="input-icon" />
                    <input
                      id="email"
                      name="email"
                      type="email"
                      autoComplete="email"
                      className="input input-with-icon input-lg"
                      placeholder="you@company.com"
                      value={email}
                      onChange={(e) => setEmail(e.target.value)}
                      onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), handleEmailNext())}
                      autoFocus
                      required
                    />
                  </div>
                </div>

                <button
                  type="button"
                  className="btn btn-primary btn-xl"
                  onClick={handleEmailNext}
                  disabled={!email || isDiscovering}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  {isDiscovering ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span className="spinner" style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                      Checking...
                    </span>
                  ) : (
                    'Next'
                  )}
                </button>
              </>
            )}

            {/* Step 2a: SSO Discovered */}
            {loginStep === 'sso' && ssoDiscovery && (
              <>
                <div style={{
                  padding: '16px',
                  background: 'rgba(14, 165, 233, 0.08)',
                  border: '1px solid rgba(14, 165, 233, 0.2)',
                  borderRadius: '8px',
                  textAlign: 'center',
                  marginBottom: '8px',
                }}>
                  <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '4px' }}>
                    {email}
                  </p>
                  <p style={{ fontWeight: 600, fontSize: '16px' }}>
                    Sign in with {ssoDiscovery.tenant_name}
                  </p>
                </div>

                {ssoError && (
                  <div className="alert alert-error" style={{ marginBottom: '8px' }}>
                    {ssoError}
                  </div>
                )}

                <button
                  type="button"
                  className="btn btn-primary btn-xl"
                  onClick={handleSSOLogin}
                  style={{ width: '100%' }}
                >
                  Continue with {ssoDiscovery.provider_name}
                </button>

                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={() => { setLoginStep('password'); setSsoError(null); }}
                  style={{ width: '100%', marginTop: '8px', fontSize: '13px' }}
                >
                  Use password instead
                </button>

                <button
                  type="button"
                  className="btn btn-ghost"
                  onClick={handleResetEmail}
                  style={{ width: '100%', fontSize: '13px' }}
                >
                  &larr; Use a different email
                </button>
              </>
            )}

            {/* Step 2b: Password (existing flow) */}
            {loginStep === 'password' && (
              <>
                {/* Email display (read-only) */}
                <div className="input-group">
                  <label className="input-label" htmlFor="email-display">Email</label>
                  <div className="input-wrapper">
                    <MailIcon size={18} className="input-icon" />
                    <input
                      id="email-display"
                      type="email"
                      className="input input-with-icon input-lg"
                      value={email}
                      readOnly
                      style={{ opacity: 0.7 }}
                    />
                  </div>
                  <button
                    type="button"
                    className="btn btn-ghost"
                    onClick={handleResetEmail}
                    style={{ fontSize: '12px', marginTop: '4px', padding: '4px 8px' }}
                  >
                    Change email
                  </button>
                </div>

                {/* Password Input */}
                <div className="input-group">
                  <label className="input-label" htmlFor="password">Password</label>
                  <div className="input-wrapper">
                    <LockIcon size={18} className="input-icon" />
                    <input
                      id="password"
                      name="password"
                      type={showPassword ? 'text' : 'password'}
                      autoComplete="current-password"
                      className="input input-with-icon input-lg"
                      placeholder="Enter your password"
                      value={password}
                      onChange={(e) => setPassword(e.target.value)}
                      autoFocus
                      required
                      style={{ paddingRight: '48px' }}
                    />
                    <button
                      type="button"
                      className="input-icon-right"
                      onClick={() => setShowPassword(!showPassword)}
                      style={{ background: 'none', border: 'none', cursor: 'pointer' }}
                    >
                      {showPassword ? <EyeOffIcon size={18} /> : <EyeIcon size={18} />}
                    </button>
                  </div>
                </div>

                {/* Remember & Forgot */}
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: '8px', cursor: 'pointer' }}>
                    <input
                      type="checkbox"
                      checked={rememberMe}
                      onChange={(e) => setRememberMe(e.target.checked)}
                      style={{
                        width: '18px',
                        height: '18px',
                        accentColor: 'var(--accent-primary)',
                        cursor: 'pointer'
                      }}
                    />
                    <span style={{ fontSize: 'var(--text-sm)', color: 'var(--text-secondary)' }}>
                      Keep me signed in
                    </span>
                  </label>
                  <a
                    href="#forgot"
                    style={{
                      fontSize: 'var(--text-sm)',
                      color: 'var(--accent-primary)',
                      fontWeight: 500
                    }}
                  >
                    Forgot password?
                  </a>
                </div>

                {/* Submit Button */}
                <button
                  type="submit"
                  className="btn btn-primary btn-xl"
                  disabled={isLoading}
                  style={{ width: '100%', marginTop: '8px' }}
                >
                  {isLoading ? (
                    <span style={{ display: 'flex', alignItems: 'center', gap: '8px', justifyContent: 'center' }}>
                      <span className="spinner" style={{
                        width: '18px',
                        height: '18px',
                        border: '2px solid rgba(255,255,255,0.3)',
                        borderTopColor: 'white',
                        borderRadius: '50%',
                        animation: 'spin 0.8s linear infinite'
                      }} />
                      Signing in...
                    </span>
                  ) : (
                    'Sign in'
                  )}
                </button>
              </>
            )}
          </form>

        </div>
      </main>

      {/* Spinner Animation */}
      <style>{`
        @keyframes spin {
          to { transform: rotate(360deg); }
        }
      `}</style>
    </div>
  );
};

export default LoginPage;
